import React, { useEffect, useState } from 'react'
import { DetailBox, PaginatedTable, TitleBar } from '../../common'
import { GROUP_BY, LOGS_DETAIL_COLUMN, POPUP_TYPE, ROUTES, TITLES } from '../../../constants'
import { useLocation } from "react-router-dom";
import { useNavigate } from "../../../backend/navigation";
import { manufacturingLogLineData } from '../../../service/logs';
import { popup } from '../../../utils';
import { useDebounce } from '../../../hooks';
import { logDetailBox, logLinesDataCompiler } from '../../../utils/dataCompiler/logsDataCompiler';
import { getGroupBy } from '../../../service';

export const LogDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [lineData, setLineData] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredColumn] = useState(LOGS_DETAIL_COLUMN);
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState(false);
  const [sort1, setSort1] = useState(LOGS_DETAIL_COLUMN[10].name);
  const [sortingArray1, setSortingArray1] = useState([]);
  const [groupArray, setGroupArray] = useState([]);
  const [groupBy, setGroupBy] = useState(GROUP_BY.TITLE);
  const [selectedItem, setSelectedItem] = useState([]);
  const [availableValuesGroupBy, setAvailableValuesGroupBy] = useState([]);

  const fetchGroupBY = useDebounce(() => {
    const params = {
      related_to: GROUP_BY.LOG_LINE,
      field_name: groupBy,
      log_id: state?.id
    };
    getGroupBy({ data: params })
      .then((response) => {
        if (response?.data && response?.data[groupBy]) {
          setAvailableValuesGroupBy(response?.data[groupBy]);
        }
      })
      .catch((error) => {
        console.error('Error fetching group by options:', error);
      })
  }, 400);

  const handleGroupByChange = (newGroupBy) => {
    if (newGroupBy === groupBy) return;
    setGroupBy(newGroupBy);
    setSelectedItem([]);
  };

  const removeGroupBy = () => {
      setSelectedItem([]);
      setAvailableValuesGroupBy([]);
      setGroupBy(GROUP_BY.TITLE);
  
      // Fetch data with groupBy removed
      fetchLogDetails();
    };

  const fetchLogDetails = useDebounce(() => {
    if (!state?.id) return navigate(ROUTES.LOGS);
    setIsLoading(true);
    const sortSelection = {};
    if (sort1 !== LOGS_DETAIL_COLUMN[10].name) {
      sortSelection.status = sort1.toLowerCase();
    }
    const params = {
      page_number: pageNumber || 1,
      log_id: state?.id,
      search: searchTerm
        ? {
          'product_id.default_code': searchTerm,
          'product_id.name': searchTerm,
          invoice_number: searchTerm,
          lot: searchTerm
        }
        : {},
      sort: {
        selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
      },
      groupby_field: selectedItem.length ? groupBy : '',
    };

    if (selectedItem.length) {
      if (groupBy.includes(',')) {
        const fields = groupBy.split(',');
        const groupbySpecifics = {};
        fields.forEach((field) => {
          groupbySpecifics[field.trim()] = selectedItem.map((item) =>
            Array.isArray(item) && item.length > 1
              ? isNaN(Number(item[0])) ? item[0] : Number(item[0])
              : isNaN(Number(item)) ? item : Number(item)
          );
        });
        params.groupby_specifics = groupbySpecifics;
      } else {
        params.groupby_specifics = {
          [groupBy]: selectedItem.map((item) =>
            Array.isArray(item) && item.length > 1
              ? isNaN(Number(item[0])) ? item[0] : Number(item[0])
              : isNaN(Number(item)) ? item : Number(item)
          ),
        };
      }
    } else {
      params.groupby_specifics = {};
    }

    manufacturingLogLineData({ data: params })
      .then((response) => {
        if (response?.data?.records?.length > 0) {
          setLineData(response.data.log_lines.records);
          setPaginationData(response.data.log_lines.params);
          setGroupArray(response.data.groupby);
          const statusField = LOGS_DETAIL_COLUMN[10].dataField[0];
          if (response?.data[statusField] && Array.isArray(response?.data[statusField])) {
            setSortingArray1(response.data[statusField]);
          }
        } else {
          setLineData([]);
          setPaginationData({});
        }
      })
      .catch((error) => {
        setLineData([]);
        setPaginationData({});
        popup(POPUP_TYPE.ERR, error.message);
      })
      .finally(() => setIsLoading(false));
  }, 400);

  useEffect(() => {
    setIsLoading(true);
    fetchLogDetails();
  }, [fetchLogDetails, pageNumber, search, sort1, selectedItem, state?.id]);

  useEffect(() => {
    if (!groupArray.includes(groupBy)) return;
    fetchGroupBY();
  }, [fetchGroupBY, groupBy, groupArray, state?.id]);

  const resetFilter = () => {
    setSort1(LOGS_DETAIL_COLUMN[10].name);
  };

  return (
    <>
      <TitleBar
        title={[
          { text: TITLES.LOGS, route: ROUTES.LOGS },
          { text: state?.id, route: ROUTES.NULL },
        ]}
      />

      <DetailBox
        colCount={2}
        colData={state}
        dataCompiler={logDetailBox}
      />

      <PaginatedTable
        columns={filteredColumn}
        initialData={lineData}
        PAGE_SIZE={20}
        colFilter={false}
        compiler={{ use: true, tool: logLinesDataCompiler }}
        paginationData={paginationData}
        setPageNumber={setPageNumber}
        isLoading={isLoading}
        showInlineSearch={true}
        search={search}
        setSearch={setSearch}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showFilter={true}
        showGroupBy={true}
        sort1={sort1}
        setSort1={setSort1}
        sortingArray1={sortingArray1}
        onDefaultSearch={fetchLogDetails}
        filterFieldName={LOGS_DETAIL_COLUMN[10].name}
        resetFilter={resetFilter}
        groupArray={groupArray}
        groupBy={groupBy}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        availableValuesGroupBy={availableValuesGroupBy}
        setAvailableValuesGroupBy={setAvailableValuesGroupBy}
        handleGroupByChange={handleGroupByChange}
        removeGroupBy={removeGroupBy}
      />
    </>
  )
}
